import {
  DASHBOARD_BUILDER_STORAGE_KEY,
  DASHBOARD_BUILDER_SCHEMA_VERSION,
  type DashboardBuilderState,
} from './types';
import { defaultDashboardBuilderState } from './defaultState';
import { mergeDashboardHeaderDefaults } from './dashboardHeaderDefaults';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

const REMOVED_COP_TYPES = new Set([
  'cop_map',
  'cop_live_status',
  'cop_smart_analytics',
  'cop_timeline',
  'cop_device_list',
]);

/** Remove the legacy w1 (placeholder→iframe) + w2 (Getting started text) from old default dashboards. */
export function stripLegacyDefaults(state: DashboardBuilderState): void {
  const keys = Object.keys(state.widgets);
  if (keys.length !== 2 || !('w1' in state.widgets) || !('w2' in state.widgets)) return;
  const w1 = state.widgets.w1;
  const w2 = state.widgets.w2;
  const w1IsDefault = w1.type === 'iframe' || w1.type === 'placeholder';
  const w2IsDefault = w2.type === 'text' && w2.config?.title === 'Getting started';
  if (w1IsDefault && w2IsDefault) {
    state.layout = [];
    state.widgets = {};
  }
}

function parseState(raw: unknown): DashboardBuilderState | null {
  if (!isRecord(raw)) return null;
  if (raw.version !== DASHBOARD_BUILDER_SCHEMA_VERSION) return null;
  if (!Array.isArray(raw.layout)) return null;
  if (!isRecord(raw.widgets)) return null;
  for (const item of raw.layout) {
    if (!isRecord(item)) return null;
    if (typeof item.i !== 'string') return null;
    if (typeof item.x !== 'number' || typeof item.y !== 'number') return null;
    if (typeof item.w !== 'number' || typeof item.h !== 'number') return null;
  }
  const state = raw as unknown as DashboardBuilderState;
  for (const [id, w] of Object.entries(state.widgets)) {
    if (REMOVED_COP_TYPES.has(w.type)) {
      state.widgets[id] = { type: 'placeholder' };
    }
    if (w.type === 'placeholder' as string) {
      state.widgets[id] = { type: 'iframe', config: { ...w.config } };
    }
  }
  stripLegacyDefaults(state);

  // Strip legacy workspaceKind
  delete (state as unknown as Record<string, unknown>).workspaceKind;

  // Validate widget_stack children: prune references to missing widgets
  for (const [id, w] of Object.entries(state.widgets)) {
    if (w.type === 'widget_stack' && w.config && Array.isArray(w.config.childIds)) {
      const valid = w.config.childIds.filter((cid: string) => typeof cid === 'string' && cid in state.widgets);
      w.config.childIds = valid;
      if (typeof w.config.activeChildIndex === 'number') {
        w.config.activeChildIndex = Math.min(w.config.activeChildIndex, Math.max(0, valid.length - 1));
      }
      if (valid.length === 0) {
        state.widgets[id] = { type: 'iframe', config: { ...w.config } };
      }
    }
  }

  // Validate widget_group children: prune references to missing widgets
  for (const [id, w] of Object.entries(state.widgets)) {
    if (w.type === 'widget_group' && w.config && Array.isArray(w.config.groupChildIds)) {
      const valid = w.config.groupChildIds.filter((cid: string) => typeof cid === 'string' && cid in state.widgets);
      w.config.groupChildIds = valid;
      if (valid.length === 0) {
        state.widgets[id] = { type: 'iframe', config: { ...w.config } };
      }
    }
  }

  return state;
}

export function loadDashboardBuilderState(): DashboardBuilderState {
  try {
    const s = localStorage.getItem(DASHBOARD_BUILDER_STORAGE_KEY);
    if (!s) return defaultDashboardBuilderState();
    const parsed = parseState(JSON.parse(s));
    const base = parsed ?? defaultDashboardBuilderState();
    return mergeDashboardHeaderDefaults(base);
  } catch {
    return defaultDashboardBuilderState();
  }
}

export function saveDashboardBuilderState(state: DashboardBuilderState): void {
  try {
    localStorage.setItem(DASHBOARD_BUILDER_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // quota / private mode — ignore
  }
}

export function clearDashboardBuilderState(): void {
  try {
    localStorage.removeItem(DASHBOARD_BUILDER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
